import { trackEvent } from "src/Amplitude";
import CloseIcon from "@mui/icons-material/Close";
import {
  AppBar,
  Button,
  ClickAwayListener,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  styled,
  Theme,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import React from "react";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import BrandArrowDown from "src/icons/BrandArrowDown";
import CalendarIcon from "src/icons/CalendarIcon";
import TradeIcon from "src/icons/TradeIcon";
import CryptoDepositIcon from "src/icons/transactions/CryptoDepositIcon";
import CryptoWithdrawal from "src/icons/transactions/CryptoWithdrawalIcon";
import TargetIcon from "src/icons/transactions/TargetIcon";
import { DEFAULT_TRANSACTION_PARAMS } from "src/pages/TransactionMobile";
import api from "src/store/features/api";
import { GetUserCurrencyTransactionsApiArg } from "src/store/features/api/generated";
import useInput from "src/utils/hooks/useInput";
import TransactionDateRangePicker from "./TransactionDateRangePicker";
import { FilterMobileStyles } from "./styles/FilterMobileStyles";
import { CurrencySelection } from "./CurrencySelection";
import { getDate } from "./helperfns";
import Option from "./Option";
import TransactionSizeInput from "./TransactionSizeInput";
import { RawTransactions, txType } from "./types";

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const MoblieFilterOptionsStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: (selected) => (selected ? "#e5e9eb" : "#f8f9f9"),
      border: "1px solid transparent",
      padding: "8px 12px",
      borderRadius: "10px",
      height: "43px",
      "&:hover": {
        backgroundColor: (selected: any) => (selected ? "#e5e9eb" : "#f8f9f9"),
        border: "1px solid #002035",
      },
      textTransform: "none",
    },
  }),
);

interface FilterProps {
  toggleFilter: () => void;
  showFilter: boolean;
  transactions: RawTransactions;
  currentParams: GetUserCurrencyTransactionsApiArg;
  setParams: (newParams: GetUserCurrencyTransactionsApiArg) => void;
  refetch: () => void;
  lowHigh: string[];
}

export default function FilterMobile(props: FilterProps) {
  const { currentParams, setParams, refetch, lowHigh } = props;
  const theme = useTheme();
  const classes = FilterMobileStyles();

  const [selected, setSelected] = React.useState<boolean>(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const { data: cryptoCurrencies = [] } = api.useGetCryptoCurrenciesQuery();

  const treatedCryptoCurrencies = cryptoCurrencies.filter(
    (el) =>
      el.permissions?.buy === true ||
      el?.permissions?.sell === true ||
      el?.permissions?.deposit === true ||
      el.permissions?.withdraw === true,
  );
  // Transaction type variables and functions
  const [typeSelected, setTypeSelected] = React.useState<string>("all");

  const handleTypeSelection = (type: string) => {
    setSelected(true);
    setTypeSelected(type);
  };

  // Currency selection variables and functions
  const [currencyTitle, setCurrencyTitle] = React.useState("All");
  const [currencyIcon, setCurrencyIcon] = React.useState("All");
  const [currency, setCurrency] = React.useState<string>("");
  const [openCurrencySelection, setOpenCurrencySelection] = React.useState<boolean>(false);
  const [currencySelected, setCurrencySelected] = React.useState<boolean>(false);

  const handleCurrencyRemoveSelected = () => {
    if (currencyTitle === "All") setCurrencySelected(false);
  };

  const handleCurrencySelection = (e: any) => {
    setSelected(true);
    setCurrencySelected(true);
    setOpenCurrencySelection((prev) => !prev);
  };

  const dropDownIcon = () => {
    if (currencyIcon === "All") {
      return <BrandArrowDown text="dark" />;
    }
    return <CountryOrCryptoFlag name={currencyIcon} size={15} />;
  };

  const handleFiatCurrencyFilter = (icon: string | null, code: string): void => {
    setSelected(true);
    setCurrencyTitle(code);
    setCurrencyIcon(icon ? icon : code);
    setCurrency(code === "All" ? "" : code);
  };

  const currencyData = React.useMemo(() => {
    if (!treatedCryptoCurrencies) return [];
    // Filter currencies based on the search query
    const filteredCurrencies = treatedCryptoCurrencies.filter(
      (curr) =>
        curr.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (curr.displayName && curr.displayName.toLowerCase().includes(searchQuery.toLowerCase())),
    );

    return filteredCurrencies.map((curr) => {
      return {
        clickValue: curr.code,
        img: curr.logoUrl ?? curr.code,
        listPrimary: curr.code,
        listSecondary: curr.displayName ?? "",
      };
    });
  }, [treatedCryptoCurrencies, searchQuery]);

  // Date selection variables and functions
  const [dateTitle, setDateTitle] = React.useState("Select");
  const [openDates, setOpenDates] = React.useState<boolean>(false);
  const [startDate, setStartDate] = React.useState<number | undefined>();
  const [endDate, setEndDate] = React.useState<number | undefined>();
  const [dateSelected, setDateSelected] = React.useState<boolean>(false);

  const handleDateRemoveSelected = () => {
    if (dateTitle === "Select") setDateSelected(false);
  };

  const handleDates = () => {
    trackEvent("Select_Transaction_Page_Dates_Filter");
    setSelected(true);
    setOpenDates((prev) => !prev);
  };

  const handleChangeDate = React.useCallback(
    (start: number, end: number) => {
      setStartDate(start);
      setEndDate(end);
      const s = getDate(start);
      const e = getDate(end);
      setDateSelected(true);
      setDateTitle(`${s} - ${e}`);
    },
    [currentParams.startDate, currentParams.endDate],
  );

  // Transaction minimum and maximum variables and functions
  const [minError, setMinError] = React.useState<boolean>(false);
  const [maxError, setMaxError] = React.useState<boolean>(false);

  const {
    reset: clearMin,
    reseted: resetterMin,
    value: valueMin,
    inputBlurHandler: BlurHandlerMin,
    valueChangeHandler: changeHandlerMin,
  } = useInput();

  const {
    reset: clearMax,
    reseted: resetterMax,
    value: valueMax,
    inputBlurHandler: BlurHandlerMax,
    valueChangeHandler: changeHandlerMax,
  } = useInput();

  React.useEffect(() => {
    if (!valueMin || !valueMax) {
      setMinError(false);
      setMaxError(false);
    }

    if (valueMax && valueMin && valueMax < valueMin) {
      setMinError(true);
      setMaxError(true);
      return;
    } else {
      setMinError(false);
      setMaxError(false);
    }
  }, [valueMin, valueMax]);

  const handleClearFilter = () => {
    setCurrencyTitle("All");
    setCurrencyIcon("All");
    setDateTitle("Select");
    setTypeSelected("all");
    clearMin();
    clearMax();
    setSelected(false);
    setCurrencySelected(false);
    setDateSelected(false);
    setParams(DEFAULT_TRANSACTION_PARAMS);
    refetch();
  };

  const handleConfirmation = () => {
    const min = valueMin as number | undefined;
    const max = valueMax as number | undefined;
    setParams({
      currency: currency,
      txType: typeSelected.toLowerCase() as txType,
      startDate,
      endDate,
      startTxSize: min,
      endTxSize: max,
    });
    setSelected(false);
    props.toggleFilter();
  };

  const txTypeButton = [
    {
      text: "Deposits",
      txType: "deposit",
      icon: CryptoDepositIcon,
    },
    {
      text: "Trades",
      txType: "trade",
      icon: TradeIcon,
    },
    {
      text: "Withdrawals",
      txType: "withdrawal",
      icon: CryptoWithdrawal,
    },
    {
      text: "Targets",
      txType: "target",
      icon: TargetIcon,
    },
  ];

  return (
    <div>
      <Dialog fullScreen open={props.showFilter}>
        <AppBar sx={{ position: "relative", marginBottom: "24px" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={props.toggleFilter}
              aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ mr: 3, flex: 1, textAlign: "center" }} variant="h6" component="div">
              Filter
            </Typography>
          </Toolbar>
        </AppBar>

        <Container>
          {/* SELECT CURRENCY */}
          <Grid item container>
            <Grid item container justifyContent="space-between" sx={{ padding: "0px 8px" }}>
              <Grid item>
                <Typography className={classes.title}>Transaction type</Typography>
              </Grid>
              <Grid item>
                <Typography
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      color: theme.palette.secondary.main,
                    },
                  }}
                  onClick={handleClearFilter}>
                  <u>Clear</u>
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={1}>
              {txTypeButton.map((item) => (
                <Grid key={item.text} item xs={6}>
                  <Item
                    id={`transactions-filter-${item.txType}`}
                    className={classes.item}
                    onClick={() => handleTypeSelection(item.txType)}
                    sx={{
                      border: "1px solid #e5e9eb",
                      backgroundColor: typeSelected === item.txType ? "#e5e9eb" : "unset",
                    }}>
                    <item.icon width={"32px"} height={"32px"} background={true} />
                    <Typography sx={{ color: "#3D4346" }}>{item.text}</Typography>
                  </Item>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* SELECT CURRENCY */}
          <Grid item container sx={{ padding: "8px 0px" }}>
            <Grid item>
              <Typography className={classes.title}>Currency</Typography>
            </Grid>
            <ClickAwayListener onClickAway={handleCurrencyRemoveSelected}>
              <Grid item xs={12}>
                <Option
                  name=""
                  defaultOption={currencyTitle}
                  icon={dropDownIcon()}
                  onClick={handleCurrencySelection}
                  width="100%"
                  fullWidth
                  isSelected={currencySelected}
                  buttonStyles={MoblieFilterOptionsStyles}
                />

                <CurrencySelection
                  open={openCurrencySelection}
                  setOpen={setOpenCurrencySelection}
                  data={currencyData}
                  onSearch={setSearchQuery}
                  onClick={handleFiatCurrencyFilter}
                  all
                  subtitle="Select currency"
                />
              </Grid>
            </ClickAwayListener>
          </Grid>

          {/* SELECT DATE */}
          <Grid item container sx={{ padding: "8px 0px" }}>
            <Grid item>
              <Typography className={classes.title}>Date</Typography>
            </Grid>
            <ClickAwayListener onClickAway={handleDateRemoveSelected}>
              <Grid item xs={12}>
                <Option
                  name=""
                  defaultOption={dateTitle}
                  icon={<CalendarIcon height="15px" />}
                  onClick={handleDates}
                  width="100%"
                  fullWidth
                  isSelected={dateSelected}
                  buttonStyles={MoblieFilterOptionsStyles}
                />

                <TransactionDateRangePicker
                  openCalendar={openDates}
                  setOpenCalendar={setOpenDates}
                  callback={handleChangeDate}
                />
              </Grid>
            </ClickAwayListener>
          </Grid>

          {/* SELECT TRANSACTION */}
          <Grid item container sx={{ padding: "8px 0px" }}>
            <Grid item>
              <Typography className={classes.title}>Transaction size</Typography>
            </Grid>
            <Grid item container justifyContent="space-between" alignItems="center">
              <Grid item xs pr="10px" sx={{ height: "43px" }}>
                <TransactionSizeInput
                  placeHolder={lowHigh[0]}
                  value={valueMin}
                  hasError={minError}
                  reseted={resetterMin}
                  onChange={changeHandlerMin}
                  inputBlurHandler={BlurHandlerMin}
                />
              </Grid>
              <Grid item sx={{ color: "#bdc2c5" }}>
                to
              </Grid>
              <Grid item xs pl="10px" sx={{ height: "43px" }}>
                <TransactionSizeInput
                  id="transaction-size-max"
                  placeHolder={lowHigh[1]}
                  value={valueMax}
                  hasError={maxError}
                  reseted={resetterMax}
                  onChange={changeHandlerMax}
                  inputBlurHandler={BlurHandlerMax}
                />
              </Grid>
            </Grid>

            {/* IF ERROR OCCURS FROM TRANSACTION SIZE */}
            <Grid container sx={{ height: "70px" }}>
              {minError && maxError && (
                <Grid
                  item
                  sx={{
                    color: theme.palette.secondary.main,
                    fontSize: "14px",
                    padding: "12px 12px",
                  }}>
                  The value of your maximum amount is lower than your minimum amount. Please enter a
                  valid maximum amount to proceed.
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} mb={2} sx={{ marginTop: "32px" }}>
            <Button
              disableRipple
              fullWidth
              onClick={handleConfirmation}
              sx={{
                border: "1px solid #fc4c4c",
                borderRadius: "10px",
                color: selected ? "#fff" : "#fc4c4c",
                textTransform: "none",
                fontWeight: 600,

                boxShadow: selected ? "0 4px 4px 0 rgba(215, 192, 192, 0.5)" : "none",
                backgroundColor: selected ? "#fc4c4c" : "unset",
              }}>
              Confirm
            </Button>
          </Grid>
        </Container>
      </Dialog>
    </div>
  );
}
